import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import {
  FaCalendarCheck,
  FaPlus,
  FaSave,
  FaTrash,
  FaCloudUploadAlt,
  FaCheckCircle,
  FaSearch,
  FaInfoCircle
} from "react-icons/fa";
import "../styles/ManageFestival.css";

const ManageFestivals = () => {
  /* ================= STATE ================= */
  const [headerTitle, setHeaderTitle] = useState("");
  const [headerDesc, setHeaderDesc] = useState("");
  const [festivals, setFestivals] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Redesign Tab & Filter states
  const [activeTab, setActiveTab] = useState("cards"); // "cards" | "header"
  const [searchQuery, setSearchQuery] = useState("");

  /* ================= FETCH DATA ================= */
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(API_ENDPOINTS.FESTIVALS);
        if (res.data) {
          setHeaderTitle(res.data.headerTitle || "");
          setHeaderDesc(res.data.headerDesc || "");
          setFestivals(res.data.festivals || []);
        }
      } catch (err) {
        console.log("Fetch Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  /* ================= FESTIVAL UPDATE ================= */
  const updateFestival = (index, field, value) => {
    const updated = [...festivals];
    updated[index][field] = value;
    setFestivals(updated);
  };

  /* ================= IMAGE UPLOAD ================= */
  const handleFestivalImage = async (index, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    try {
      const res = await axios.post(API_ENDPOINTS.UPLOAD, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const updated = [...festivals];
      updated[index].image = res.data.imageUrl;
      updated[index].preview = res.data.imageUrl;
      setFestivals(updated);
    } catch (err) {
      console.log("Upload Error:", err);
      alert("Image upload failed");
    }
  };

  /* ================= ADD FESTIVAL ================= */
  const addFestival = () => {
    setFestivals([
      {
        title: "",
        image: "",
        preview: "",
        description: "",
        highlights: [""],
        rituals: [""],
      },
      ...festivals,
    ]);
    setActiveTab("cards");
  };

  /* ================= DELETE FESTIVAL ================= */
  const deleteFestival = (index) => {
    if (window.confirm("Are you sure you want to delete this festival?")) {
      setFestivals(festivals.filter((_, i) => i !== index));
    }
  };

  /* ================= HIGHLIGHTS ================= */
  const updateHighlight = (fi, hi, value) => {
    const updated = [...festivals];
    updated[fi].highlights[hi] = value;
    setFestivals(updated);
  };

  const addHighlight = (fi) => {
    const updated = [...festivals];
    if (!updated[fi].highlights) updated[fi].highlights = [];
    updated[fi].highlights.push("");
    setFestivals(updated);
  };

  const deleteHighlight = (fi, hi) => {
    const updated = [...festivals];
    updated[fi].highlights = updated[fi].highlights.filter((_, i) => i !== hi);
    setFestivals(updated);
  };

  /* ================= RITUALS ================= */
  const updateRitual = (fi, ri, value) => {
    const updated = [...festivals];
    updated[fi].rituals[ri] = value;
    setFestivals(updated);
  };

  const addRitual = (fi) => {
    const updated = [...festivals];
    if (!updated[fi].rituals) updated[fi].rituals = [];
    updated[fi].rituals.push("");
    setFestivals(updated);
  };

  const deleteRitual = (fi, ri) => {
    const updated = [...festivals];
    updated[fi].rituals = updated[fi].rituals.filter((_, i) => i !== ri);
    setFestivals(updated);
  };

  /* ================= SAVE ================= */
  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveSuccess(false);

      const payload = {
        headerTitle,
        headerDesc,
        festivals: festivals.map((f) => ({
          title: f.title,
          image: f.image || "",
          description: f.description,
          highlights: f.highlights || [],
          rituals: f.rituals || [],
        })),
      };

      await axios.post(API_ENDPOINTS.FESTIVALS, payload);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      alert("🎉 Festival Page Saved Successfully!");
    } catch (err) {
      console.log(err);
      alert("❌ Save Failed");
    } finally {
      setSaving(false);
    }
  };

  /* ================= RENDER LOADING ================= */
  if (loading) {
    return (
      <div className="festivals-loading">
        <div className="spinner"></div>
        <h2>Loading Festivals Content...</h2>
        <p>Fetching temple events data</p>
      </div>
    );
  }

  /* ================= FILTER LOGIC ================= */
  const filteredFestivals = festivals.filter(f => 
    f.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="manage-festival-container">
      {/* BANNER SECTION */}
      <div className="festival-banner">
        <div className="banner-content">
          <h1>🎉 Manage Festivals</h1>
          <p>Add, edit, and organize important temple festivals, details, background banners, highlights, and specific rituals.</p>
        </div>
        <div className="banner-stats">
          <div className="stat-pill">
            <FaCalendarCheck className="icon" />
            <span><strong>{festivals.length}</strong> Festivals</span>
          </div>
        </div>
      </div>

      {/* DASHBOARD CONTROLS & TAB SWITCHER */}
      <div className="dashboard-controls">
        <div className="tabs-wrapper">
          <button 
            className={`tab-btn ${activeTab === "cards" ? "active" : ""}`}
            onClick={() => setActiveTab("cards")}
          >
            <FaCalendarCheck /> Festival Cards
          </button>
          <button 
            className={`tab-btn ${activeTab === "header" ? "active" : ""}`}
            onClick={() => setActiveTab("header")}
          >
            <FaInfoCircle /> Page Header
          </button>
        </div>

        {activeTab === "cards" && (
          <div className="search-filter-box">
            <div className="search-input-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search festivals by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        )}

        <div className="quick-action-buttons">
          {activeTab === "cards" && (
            <button className="action-btn add-btn-primary" onClick={addFestival}>
              <FaPlus /> Add Festival
            </button>
          )}
          <button 
            className={`action-btn save-btn-primary ${saving ? "saving" : ""} ${saveSuccess ? "saved" : ""}`}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <div className="spinner-small"></div> Saving...
              </>
            ) : saveSuccess ? (
              <>
                <FaCheckCircle /> Saved!
              </>
            ) : (
              <>
                <FaSave /> Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* PANEL CONTAINER */}
      <div className="panel-container">
        
        {/* PANEL: PAGE HEADER */}
        {activeTab === "header" && (
          <div className="panel-card fade-in">
            <div className="panel-card-header">
              <h3><FaInfoCircle className="title-icon" /> Festival Page Header</h3>
              <p>Configure the header texts that appear at the top of the festivals web page.</p>
            </div>
            
            <div className="panel-card-body">
              <div className="form-group">
                <label>Header Title</label>
                <input
                  type="text"
                  value={headerTitle}
                  onChange={(e) => setHeaderTitle(e.target.value)}
                  placeholder="e.g. Auspicious Festivals & Celebrations"
                />
              </div>

              <div className="form-group">
                <label>Header Description</label>
                <textarea
                  value={headerDesc}
                  onChange={(e) => setHeaderDesc(e.target.value)}
                  placeholder="Describe the spiritual significance of temple festivals..."
                  rows={5}
                />
              </div>
            </div>
          </div>
        )}

        {/* PANEL: FESTIVAL CARDS */}
        {activeTab === "cards" && (
          <div className="directory-section fade-in">
            {filteredFestivals.length === 0 ? (
              <div className="empty-state">
                <FaCalendarCheck className="empty-icon" />
                <h3>No Festivals Found</h3>
                <p>{searchQuery ? "Try searching for a different keyword." : "Add a new temple festival card to publish on the website."}</p>
                <button className="action-btn add-btn-primary" onClick={addFestival}>
                  <FaPlus /> Add Festival Now
                </button>
              </div>
            ) : (
              <div className="festival-layout-grid">
                {filteredFestivals.map((festival, index) => (
                  <div className="festival-entry-card" key={index}>
                    <div className="festival-card-top-header">
                      <span className="card-number">Festival #{index + 1}</span>
                      <button className="card-delete-btn" onClick={() => deleteFestival(index)}>
                        <FaTrash /> Delete
                      </button>
                    </div>

                    <div className="festival-card-body">
                      {/* Form Details */}
                      <div className="form-group-compact">
                        <label>Festival Name / Title</label>
                        <input
                          type="text"
                          value={festival.title}
                          onChange={(e) => updateFestival(index, "title", e.target.value)}
                          placeholder="e.g. Ram Navami"
                        />
                      </div>

                      <div className="form-group-compact">
                        <label>Description</label>
                        <textarea
                          value={festival.description}
                          onChange={(e) => updateFestival(index, "description", e.target.value)}
                          placeholder="Provide details about significance, historical stories..."
                          rows={3}
                        />
                      </div>

                      {/* Image Upload Block */}
                      <div className="form-group-compact">
                        <label>Festival Banner Image</label>
                        <div className="image-uploader-split">
                          <label className="custom-file-upload">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFestivalImage(index, e.target.files[0])}
                            />
                            <FaCloudUploadAlt className="icon" />
                            <span>Choose Image File</span>
                          </label>
                          
                          {festival.image && (
                            <div className="preview-mini-card">
                              <img src={festival.image} alt="Preview" className="preview-mini-img" />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* NESTED HIGHLIGHTS */}
                      <div className="nested-list-box">
                        <div className="nested-list-header">
                          <h4>✨ Key Highlights</h4>
                          <button className="action-btn-small" onClick={() => addHighlight(index)}>
                            <FaPlus /> Add Highlight
                          </button>
                        </div>
                        
                        {(!festival.highlights || festival.highlights.length === 0) ? (
                          <div className="empty-nested-state">No highlights added yet.</div>
                        ) : (
                          <div className="nested-items-column">
                            {festival.highlights.map((h, hi) => (
                              <div className="nested-item-row" key={hi}>
                                <input
                                  type="text"
                                  value={h}
                                  placeholder={`Highlight #${hi + 1}`}
                                  onChange={(e) => updateHighlight(index, hi, e.target.value)}
                                />
                                <button className="nested-delete-btn" onClick={() => deleteHighlight(index, hi)}>
                                  <FaTrash />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* NESTED RITUALS */}
                      <div className="nested-list-box">
                        <div className="nested-list-header">
                          <h4>🪔 Associated Rituals</h4>
                          <button className="action-btn-small" onClick={() => addRitual(index)}>
                            <FaPlus /> Add Ritual
                          </button>
                        </div>
                        
                        {(!festival.rituals || festival.rituals.length === 0) ? (
                          <div className="empty-nested-state">No rituals added yet.</div>
                        ) : (
                          <div className="nested-items-column">
                            {festival.rituals.map((r, ri) => (
                              <div className="nested-item-row" key={ri}>
                                <input
                                  type="text"
                                  value={r}
                                  placeholder={`Ritual #${ri + 1}`}
                                  onChange={(e) => updateRitual(index, ri, e.target.value)}
                                />
                                <button className="nested-delete-btn" onClick={() => deleteRitual(index, ri)}>
                                  <FaTrash />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* FLOATING SAVE BAR */}
      <div className="floating-save-bar">
        <div className="save-bar-info">
          <span>Unsaved changes to festivals will be lost.</span>
        </div>
        <button 
          className={`action-btn save-btn-primary ${saving ? "saving" : ""} ${saveSuccess ? "saved" : ""}`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <div className="spinner-small"></div> Saving...
            </>
          ) : saveSuccess ? (
            <>
              <FaCheckCircle /> Saved Successfully!
            </>
          ) : (
            <>
              <FaSave /> Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ManageFestivals;
